"use client";

import React, { memo } from "react";
import { motion } from "framer-motion";
import AIChartRenderer from "./AIChartRenderer";
import AIDataTableRenderer from "./AIDataTableRenderer";
import AIImageRenderer from "./AIImageRenderer";

interface ToolCallResult {
  toolName: string;
  toolCallId: string;
  state: "start" | "stream" | "partial-call" | "call" | "result" | "error";
  args?: any;
  result?: any;
  error?: string;
}

interface GenUIRendererProps {
  toolInvocations?: ToolCallResult[];
}

// Helper type for tool invocations
interface ToolInvocation {
  toolName: string;
  toolCallId: string;
  state: "start" | "stream" | "partial-call" | "call" | "result" | "error";
  args?: any;
  result?: any;
}

// Default suggestions to add when missing but needed - with more specific query patterns
const DEFAULT_CHART_SUGGESTIONS = [
  {
    chartType: "bar",
    title: "Bar Chart Comparison",
    reason: "Bar charts effectively compare values across different categories at a glance.",
    query: "Convert this data to a bar chart visualization"
  },
  {
    chartType: "pie",
    title: "Pie Chart Distribution",
    reason: "Pie charts show proportional distribution and are great for showing part-to-whole relationships.",
    query: "Convert this data to a pie chart to show distribution"
  },
  {
    chartType: "line",
    title: "Line Chart Trend",
    reason: "Line charts excel at showing trends over time or sequences.",
    query: "Convert this data to a line chart to show trends"
  },
  {
    chartType: "scatter",
    title: "Scatter Plot Correlation",
    reason: "Scatter plots are ideal for showing the relationship or correlation between two variables.",
    query: "Convert this data to a scatter plot to show correlation"
  }
];

// Default table suggestions
const DEFAULT_TABLE_SUGGESTIONS = [
  {
    tableType: "basic",
    title: "Basic Data Table",
    reason: "A simple table view with sorting and filtering capabilities for easy data exploration.",
    query: "Show this data in a basic table format with sorting options for each column"
  },
  {
    tableType: "advanced",
    title: "Advanced Data Table",
    reason: "An enhanced table with search, filtering, and pagination for better data analysis.",
    query: "Display this data in an advanced table format with search and filter functionalities"
  },
  {
    tableType: "custom",
    title: "Custom Data Table",
    reason: "A customizable table that allows users to focus on specific columns and data points.",
    query: "Create a custom table format where users can select which columns to display"
  }
];

// Component to render different tool outputs as UI components
const GenUIRenderer = memo(({ toolInvocations }: GenUIRendererProps) => {
  // Check console log to see when component is rendering
  console.log("GenUIRenderer actually rendering at:", new Date().toISOString());
  
  if (!toolInvocations || !Array.isArray(toolInvocations) || toolInvocations.length === 0) {
    return null;
  }
  
  // Find chart, table, and image tools
  const chartTool = toolInvocations.find(tool => 
    tool.toolName === 'search_and_generate_chart' || 
    tool.toolName.includes('chart')
  );
  
  const tableTool = toolInvocations.find(tool =>
    tool.toolName === 'search_and_generate_table' ||
    tool.toolName.includes('table')
  );

  const imageTool = toolInvocations.find(tool =>
    tool.toolName === 'generate_image_from_data' ||
    tool.toolName.includes('image')
  );
  
  if (!chartTool && !tableTool && !imageTool) return null;
  
  // Render the tools found in the tool invocations
  return (
    <div className="w-full space-y-4 my-2">
      {chartTool && chartTool.result && (
        (() => {
          // For chart data rendering
          if (chartTool.result.chartType && (chartTool.result.data || Array.isArray(chartTool.result.data))) {
            // Check for show_suggestions flag in both args and config
            const needsSuggestions = 
              // Check if suggestions are explicitly enabled from args passed to API
              (chartTool.args?.show_suggestions === true || 
               chartTool.result.config?.show_suggestions === true);
              
            console.log("GenUIRenderer: Chart tool args:", chartTool.args);
            console.log("GenUIRenderer: Show suggestions flag:", chartTool.args?.show_suggestions, "Needs suggestions:", needsSuggestions);
            console.log("GenUIRenderer: Existing config suggestions:", chartTool.result.config?.suggestions);
            
            // Adding suggestions for specific types of data if missing
            if (needsSuggestions && 
                (!chartTool.result.config?.suggestions || 
                 !Array.isArray(chartTool.result.config.suggestions) ||
                 chartTool.result.config.suggestions.length === 0)) {
              
              console.log("Adding specific suggestions to chart");
              
              // Extract information from the chart title or data to make suggestions more relevant
              const chartTitle = chartTool.result.config?.title || "";
              const chartSubject = chartTitle.replace(/chart|analysis|visualization|data/ig, '').trim();
              
              // Deep clone the chart result to avoid mutating the original
              const enhancedResult = JSON.parse(JSON.stringify(chartTool.result));
              enhancedResult.config = enhancedResult.config || {};
              enhancedResult.config.suggestions = [];
              
              // This is critical: explicitly set the show_suggestions flag to true in the config
              enhancedResult.config.show_suggestions = true;
              
              // Add default suggestions
              enhancedResult.config.suggestions = [...DEFAULT_CHART_SUGGESTIONS];
              
              return <AIChartRenderer chartData={enhancedResult} />;
            }
            
            return <AIChartRenderer chartData={chartTool.result} />;
                }
          return null;
        })()
      )}

      {tableTool && tableTool.result && (
        (() => {
          // For table data rendering
          if (tableTool.result.data && Array.isArray(tableTool.result.data)) {
            // Check for show_suggestions flag in both args and config
            const needsSuggestions = 
              // Check if suggestions are explicitly enabled from args passed to API
              (tableTool.args?.show_suggestions === true || 
               tableTool.result.config?.show_suggestions === true);
              
            console.log("GenUIRenderer: Table tool args:", tableTool.args);
            console.log("GenUIRenderer: Show suggestions flag:", tableTool.args?.show_suggestions, "Needs suggestions:", needsSuggestions);
            console.log("GenUIRenderer: Existing config suggestions:", tableTool.result.config?.suggestions);
            
            // Deep clone the table result to avoid mutating the original
            const enhancedResult = JSON.parse(JSON.stringify(tableTool.result));
              enhancedResult.config = enhancedResult.config || {};
              
            // This is critical: explicitly set the show_suggestions flag to true in the config
              enhancedResult.config.show_suggestions = true;
              
            // Only add default suggestions if there are no suggestions from the LLM
            if (!enhancedResult.config.suggestions || 
                !Array.isArray(enhancedResult.config.suggestions) ||
                enhancedResult.config.suggestions.length === 0) {
              
              console.log("No LLM suggestions found, adding default suggestions");
              enhancedResult.config.suggestions = [...DEFAULT_TABLE_SUGGESTIONS];
            } else {
              console.log("Using LLM suggestions:", enhancedResult.config.suggestions);
            }
            
            console.log("GenUIRenderer: Final enhanced result:", enhancedResult);
            console.log("GenUIRenderer: Final config:", enhancedResult.config);
            console.log("GenUIRenderer: Final suggestions:", enhancedResult.config.suggestions);
            
            return <AIDataTableRenderer tableData={enhancedResult} />;
          }
          return null;
        })()
      )}

      {imageTool && imageTool.result && (
        (() => {
          // For image data rendering
          if (imageTool.result.imageUrl) {
            console.log("GenUIRenderer: Image tool result:", imageTool.result);
            return <AIImageRenderer imageData={imageTool.result} />;
          }
          return null;
        })()
      )}
    </div>
  );
});

// Set displayName for debugging
GenUIRenderer.displayName = "GenUIRenderer";

export default GenUIRenderer;

// Individual tool renderer based on tool type
function ToolRenderer({ toolInvocation }: { toolInvocation: ToolCallResult }) {
  const { toolName, toolCallId, state, args, result, error } = toolInvocation;
  
  // Show error state if applicable
  if (error || state === 'error') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-red-50 dark:bg-red-900/20 p-3 rounded-md"
      >
        <p className="text-sm text-red-600 dark:text-red-400">
          {error || "Error generating visualization"}
        </p>
      </motion.div>
    );
  }
  
  // Loading state for any tool
  if (state !== 'result') {
    return (
      <motion.div
        initial={{ opacity: 0.5, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-muted/20 p-3 rounded-md flex items-center space-x-2"
      >
        <div className="animate-spin h-4 w-4 border-2 border-primary rounded-full border-t-transparent" />
        <span className="text-sm text-muted-foreground">
          {getLoadingMessage(toolName)}
        </span>
      </motion.div>
    );
  }
  
  // Handle chart visualization tool
  if (toolName === 'search_and_generate_chart' || toolName.includes('chart')) {
    // For chart tools, pass the result directly to AIChartRenderer
    // The result should be a properly structured object with chartType, data, and config
    if (result && (result.data || result.chartType)) {
      return <AIChartRenderer chartData={result} />;
    }
    
    // If no valid chart data, show an error
    return (
      <motion.div 
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-md"
      >
        <p className="text-sm text-yellow-600 dark:text-yellow-400">
          {result?.message || "No data available for chart visualization"}
        </p>
      </motion.div>
    );
  }
  
  // Handle table visualization tool
  if (toolName === 'search_and_generate_table' || toolName.includes('table')) {
    // For table tools, pass the result directly to AIDataTableRenderer
    // The result should be a properly structured object with data and config
    if (result && result.data && Array.isArray(result.data)) {
      return <AIDataTableRenderer tableData={result} />;
    }
    
    // If no valid table data, show an error
    return (
      <motion.div 
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-md"
      >
        <p className="text-sm text-yellow-600 dark:text-yellow-400">
          {result?.message || "No data available for table visualization"}
        </p>
      </motion.div>
    );
  }
  
  // Default fallback for unhandled tool types
  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-muted/10 p-3 rounded-md"
    >
      <p className="text-sm text-muted-foreground mb-1">
        Tool result from {toolName}:
      </p>
      <pre className="bg-muted/30 p-2 rounded text-xs overflow-auto">
        {JSON.stringify(result, null, 2)}
      </pre>
    </motion.div>
  );
}

// Helper to get appropriate loading messages based on tool type
function getLoadingMessage(toolName: string): string {
  if (!toolName) {
    return "Processing...";
  }
  
  if (toolName === 'search_and_generate_chart') {
    return "Searching for data and generating chart...";
  } else if (toolName.includes('chart')) {
    return "Generating chart visualization...";
  } else if (toolName === 'search_and_generate_table') {
    return "Searching for data and generating table...";
  } else if (toolName.includes('table')) {
    return "Generating table visualization...";
  } else if (toolName.includes('search')) {
    return "Searching for information...";
  } else {
    return "Processing...";
  }
} 